/**
 * RiskApplicationModal — a janela dos dados de um risco num destino (#606, E2/E3): um formulário
 * só, com as seções (Exposição · Medidas de controle · Medições · Exames · Documentos) em abas
 * verticais à esquerda — uma seção à vista por vez, cada aba dizendo o estado dela — e a barra de
 * completude no topo. "Salvar" grava tudo, e dá para salvar faltando informação e voltar depois.
 *
 * A completude é a mesma regra do servidor (`completudeDoRisco.ts`), recalculada a cada mudança;
 * cada item de "Faltam" abre a aba do campo e o foca, e um erro ao salvar abre a aba dele.
 *
 * Lê os efetivos do destino (E1) para dizer a que destino o risco vai, se ele já vale ali por
 * herança e o efeito de salvar.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { useForm, FormProvider, type FieldErrors } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { ExposureLevel, EXPOSURE_LEVEL_LABELS } from '../../types/riskApplication';
import type {
  RiskApplication,
  RiskApplicationCreatePayload,
  RiskApplicationScope,
  RiskApplicationExistingEpiRow,
  RiskApplicationMeasurementRow,
} from '../../types/riskApplication';
import type { ExamType, ReportType } from '../../types/occupationalRisk';
import {
  MEASUREMENT_ASSESSMENT_TYPES,
  type FormulaDoLimiteParaTela,
  type MeasurementAssessmentType,
} from '../../types/environmentalMeasurement';
import { RISK_MATRIX_TYPES, getMaxProbability, getMaxSeverity, pickCompleteMatrix } from '../../types/riskMatrix';
import type { RiskMatrixType } from '../../types/riskMatrix';
import { riskApplicationService } from '../../services/riskApplicationService';
import { occupationalRiskService } from '../../services/occupationalRiskService';
import { riskProtectionService } from '../../services/riskProtectionService';
import { measurementEquipmentService } from '../../services/measurementEquipmentService';
import { MeasurementEquipmentStatus } from '../../types/measurementEquipment';
import { buscarEfetivos, type Efetivos } from '../../services/efetivosService';
import { PLURAL_DO_NIVEL, contar, mesmoId } from '../applications/textosDoNivel';
import { referencia } from './textosDoDestino';
import { ResumoDoEfeito } from './DocumentosDoRisco';
import { listaDeDocumentos } from './marcacaoDeDocumentos';
import { EscolhaDeDocumentos } from './SeletorDeDocumentos';
import { completudeDe, criteriosDoRisco, type CriterioDaJanela, type SecaoDaJanela } from './completudeDoRisco';
import { BarraDeCompletude } from '../applications/BarraDeCompletude';
import type { Completude } from '../../services/efetivosService';
import { SecaoExposicao } from './steps/SecaoExposicao';
import { SecaoMedidasDeControle, type OpcaoDeEpi } from './steps/SecaoMedidasDeControle';
import { SecaoMedicoes, type OpcaoDeAparelho } from './steps/SecaoMedicoes';
import { SecaoExames } from './steps/SecaoExames';
import { PontoDaCompletude } from './steps/comum';
import { dataDoDia, diaDaData, hojeNoSeletor } from './diaDoCampo';
import { hojeEmSaoPaulo } from '../../utils/hojeEmSaoPaulo';

// ============================================================================
// TYPES
// ============================================================================

export interface MeasurementFormRow {
  /** Nulo na medição nova; a já gravada volta com o id para o servidor atualizá-la, não recriá-la. */
  medicaoId: number | null;
  measurementEquipmentId: number | null;
  /** Nulo na periculosidade: a NR-16 enquadra a atividade em vez de medir (#424) */
  value: number | null;
  unit: string;
  measuredAt: Date | null;
  observations: string;
  technique: string;
  /** O limite de tolerância que ESTA medição usou, como o laudo o imprime. */
  appliedToleranceLimit: string;
  /** Sílica livre (#459): o % de quartzo como se digita; só vale em risco com fórmula. */
  quartzPercent?: string;
  /** '' quando não informado. */
  assessmentType: MeasurementAssessmentType | '';
  storedQuantity: number | null;
  storedQuantityUnit: string;
  transportedQuantity: number | null;
  transportedQuantityUnit: string;
  riskArea: string;
  storageType: string;
  hazardousFraming: boolean;
  hazardousAnnex: string;
  unhealthyFraming: boolean;
  unhealthyDegreeApplied: string;
}

export interface FormularioDoRisco {
  exposureLevel: ExposureLevel;
  startDate: Date | null;
  endDate: Date | null;
  observations: string;
  activityProcess: string;
  /** Risk matrix override (inherited default from the parent risk). */
  riskMatrixType: RiskMatrixType | null;
  riskProbability: number | null;
  riskSeverity: number | null;
  /** string[] at form level; cast to ExamType[] on submit */
  examTypes: string[];
  /** Os documentos escolhidos em "Só nestes"; cast to ReportType[] on submit */
  reportTypes: string[];
  existingAdministrativeMeasures: string[];
  existingEngineeringMeasures: string[];
  existingAdministrativeOther: string;
  existingEngineeringOther: string;
  existingEpiIds: number[];
  proposedAdministrative: string;
  proposedEngineering: string;
  proposedEpi: string;
  measurements: MeasurementFormRow[];
}

/** O que a janela grava, menos o risco e o destino, que o apply e a edição acrescentam. */
type CamposDaAplicacao = Omit<RiskApplicationCreatePayload, 'riskId' | 'scope' | 'scopeId'>;

export interface RiskApplicationModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful save so the page can refetch. */
  onSaved: () => void;
  scope: RiskApplicationScope;
  scopeId: number;
  mode: 'apply' | 'edit';
  /** riskId — required for apply mode */
  riskId?: number;
  /** applicationId — required for edit mode */
  applicationId?: number;
  /** Risk display name shown in the title */
  riskName?: string;
  /** Risk factor name shown as a chip in the title */
  riskFactorName?: string;
}

// ============================================================================
// SEÇÕES, ÍNDICE E BARRA DE COMPLETUDE
// ============================================================================

const SECOES: { id: SecaoDaJanela; titulo: string }[] = [
  { id: 'exposicao', titulo: 'Exposição' },
  { id: 'medidas', titulo: 'Medidas de controle' },
  { id: 'medicoes', titulo: 'Medições' },
  { id: 'exames', titulo: 'Exames' },
  { id: 'documentos', titulo: 'Documentos' },
];

const idDaSecao = (secao: SecaoDaJanela): string => `secao-${secao}`;

/** "completa", "2 faltam", "não conta" — o que o índice e o título de cada seção dizem. */
function estadoDaSecao(secao: SecaoDaJanela, criterios: readonly CriterioDaJanela[]): string {
  const daSecao = criterios.filter((c) => c.secao === secao);
  if (daSecao.length === 0) return 'não conta';
  const faltam = daSecao.filter((c) => !c.preenchido).length;
  if (faltam === 0) return 'completa';
  return faltam === 1 ? '1 falta' : `${faltam} faltam`;
}

/** De que aba é cada campo do formulário: o erro ao salvar abre a aba do primeiro campo errado. */
const SECAO_DO_CAMPO: Record<keyof FormularioDoRisco, SecaoDaJanela> = {
  exposureLevel: 'exposicao',
  startDate: 'exposicao',
  endDate: 'exposicao',
  observations: 'exposicao',
  activityProcess: 'exposicao',
  riskMatrixType: 'exposicao',
  riskProbability: 'exposicao',
  riskSeverity: 'exposicao',
  existingAdministrativeMeasures: 'medidas',
  existingEngineeringMeasures: 'medidas',
  existingAdministrativeOther: 'medidas',
  existingEngineeringOther: 'medidas',
  existingEpiIds: 'medidas',
  proposedAdministrative: 'medidas',
  proposedEngineering: 'medidas',
  proposedEpi: 'medidas',
  measurements: 'medicoes',
  examTypes: 'exames',
  reportTypes: 'documentos',
};

function primeiraSecaoComErro(erros: FieldErrors<FormularioDoRisco>): SecaoDaJanela | undefined {
  const comErro = new Set(Object.keys(erros).map((campo) => SECAO_DO_CAMPO[campo as keyof FormularioDoRisco]));
  return SECOES.find((secao) => comErro.has(secao.id))?.id;
}

/** Rola até o campo e o foca; se ele mesmo não aceita foco, foca o primeiro campo dentro dele. */
function levarAoCampo(alvo: string): void {
  const elemento = document.getElementById(alvo);
  if (!elemento) return;
  elemento.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  const focavel = elemento.matches('input, textarea, button, [tabindex]')
    ? elemento
    : elemento.querySelector<HTMLElement>('input, textarea, button, [tabindex]');
  (focavel ?? elemento).focus({ preventScroll: true });
}

interface CompletudeDaJanelaProps {
  completude: Completude;
  criterios: readonly CriterioDaJanela[];
  aoEscolher: (criterio: CriterioDaJanela) => void;
}

/** A barra de completude da listagem, com a contagem, e "Faltam:" com cada item levando ao campo. */
const CompletudeDaJanela: React.FC<CompletudeDaJanelaProps> = ({ completude, criterios, aoEscolher }) => {
  const faltando = criterios.filter((c) => !c.preenchido);
  return (
    <Box sx={{ mt: 1.5 }} data-testid="completude-da-janela">
      <BarraDeCompletude completude={completude} comContagem />
      {faltando.length > 0 && (
        <Box sx={{ mt: 1, typography: 'body2' }}>
          <Box component="span" fontWeight={600}>
            Faltam:{' '}
          </Box>
          {faltando.map((criterio, i) => (
            <React.Fragment key={criterio.rotulo}>
              {i > 0 && ' · '}
              <Link component="button" type="button" variant="body2" onClick={() => aoEscolher(criterio)}>
                {criterio.rotulo}
              </Link>
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

interface AbasDaJanelaProps {
  criterios: readonly CriterioDaJanela[];
  aba: SecaoDaJanela;
  aoTrocar: (aba: SecaoDaJanela) => void;
}

/**
 * As seções em abas, cada uma com o estado dela: o check quando completa, "N faltam". Verticais à
 * esquerda; no celular, horizontais e roláveis no topo, para não espremer a seção.
 */
const AbasDaJanela: React.FC<AbasDaJanelaProps> = ({ criterios, aba, aoTrocar }) => {
  const telaEstreita = useMediaQuery(useTheme().breakpoints.down('sm'));
  return (
    <Tabs
      orientation={telaEstreita ? 'horizontal' : 'vertical'}
      variant="scrollable"
      scrollButtons={telaEstreita ? 'auto' : false}
      allowScrollButtonsMobile
      value={aba}
      onChange={(_, nova: SecaoDaJanela) => aoTrocar(nova)}
      aria-label="Seções da janela"
      sx={
        telaEstreita
          ? { borderBottom: 1, borderColor: 'divider' }
          : { borderRight: 1, borderColor: 'divider', width: 210, flexShrink: 0 }
      }
    >
      {SECOES.map((secao) => {
        const estado = estadoDaSecao(secao.id, criterios);
        return (
          <Tab
            key={secao.id}
            value={secao.id}
            id={`aba-${secao.id}`}
            aria-controls={idDaSecao(secao.id)}
            sx={{ alignItems: 'flex-start', textAlign: 'left', textTransform: 'none', minHeight: 56, py: 1 }}
            label={
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                {estado === 'completa' && <CheckCircleIcon fontSize="small" color="success" />}
                <Box component="span">
                  <Box component="span" display="block" fontWeight={600} color="text.primary">
                    {secao.titulo}
                  </Box>
                  <Box
                    component="span"
                    display="block"
                    sx={{ fontSize: 13, color: estado.includes('falt') ? 'warning.dark' : 'text.secondary' }}
                  >
                    {estado}
                  </Box>
                </Box>
              </Box>
            }
          />
        );
      })}
    </Tabs>
  );
};

// ============================================================================
// TEXTOS DO DESTINO
// ============================================================================

type EfetivosDoRisco = Efetivos<RiskApplication>;
type Herdada = EfetivosDoRisco['herdadas'][number];

function avisoDeHeranca(modo: 'apply' | 'edit', destino: EfetivosDoRisco['destino'], herdada: Herdada): string {
  const origem = referencia('de', herdada.origem.nivel, herdada.origem.nome);
  const exposicao = EXPOSURE_LEVEL_LABELS[herdada.exposureLevel];
  if (modo === 'edit') {
    return `Esta aplicação substitui, só ${referencia('em', destino.nivel, destino.nome)}, a que vem ${origem}, com exposição ${exposicao}.`;
  }
  const alvo = referencia('', destino.nivel, destino.nome);
  return (
    `Este risco já vale para ${alvo}: vem ${origem}, com exposição ${exposicao}. ` +
    `Ao salvar, o que você preencher aqui passa a valer só para ${alvo}, no lugar do que vem ${origem}. ` +
    `Os outros ${PLURAL_DO_NIVEL[destino.nivel]} continuam como estão.`
  );
}

function linhasAoSalvar(
  efetivos: EfetivosDoRisco,
  documentos: ReportType[],
  herdada: Herdada | undefined
): string[] {
  const { destino } = efetivos;
  const linhas = [
    `Vale para ${contar(efetivos.alcance.funcionarios, 'funcionário', 'funcionários')}, ${referencia('em', destino.nivel, destino.nome)}`,
  ];
  if (documentos.length > 0) {
    linhas.push(`Aparece em: ${listaDeDocumentos(documentos)}`);
  } else {
    linhas.push('Aparece em todos os documentos');
  }
  if (herdada) {
    linhas.push(
      `Substitui, só ${referencia('em', destino.nivel, destino.nome)}, a aplicação que vem ${referencia('de', herdada.origem.nivel, herdada.origem.nome)}`
    );
  }
  return linhas;
}

// ============================================================================
// VALIDATION SCHEMA
// ============================================================================

/**
 * O valor medido deixa de ser exigido quando o risco é de periculosidade — a natureza
 * do risco só é conhecida em tempo de execução e chega ao schema pelo contexto do
 * react-hook-form (#424).
 */
function isHazardousContext(context: unknown): boolean {
  return (context as { isHazardousRisk?: boolean } | undefined)?.isHazardousRisk === true;
}

/** Risco de sílica livre (#459): o limite sai do % de quartzo, que passa a ser obrigatório. */
function comFormulaNoContexto(context: unknown): boolean {
  return (context as { comFormulaDoLimite?: boolean } | undefined)?.comFormulaDoLimite === true;
}

/**
 * "Não pode ser no futuro", conferido **na hora de validar** e não na carga do módulo.
 *
 * `.max(new Date(), ...)` parecia a mesma coisa e não era: o esquema é montado uma vez, quando o
 * módulo carrega, então aquele `new Date()` virava um teto congelado no instante da carga da
 * página. O valor padrão do campo é o instante em que a caixa abre — sempre depois. Bastavam
 * segundos de aba aberta para o campo reprovar o próprio valor padrão.
 */
const NAO_E_FUTURO = 'nao-e-futuro';
const aindaNaoAconteceu = (valor: Date | null | undefined): boolean =>
  !valor || diaDaData(valor) <= hojeEmSaoPaulo();

/** Exportado para o teste: é o que prova que o teto de data não congela na carga do módulo. */
export const validationSchema = yup.object({
  exposureLevel: yup
    .mixed<ExposureLevel>()
    .oneOf(Object.values(ExposureLevel), 'Nivel de exposicao invalido')
    .required('Nivel de exposicao e obrigatorio')
    .defined(),
  startDate: yup
    .date()
    .nullable()
    .defined()
    .test(NAO_E_FUTURO, 'Data de inicio nao pode ser no futuro', aindaNaoAconteceu),
  endDate: yup
    .date()
    .nullable()
    .defined()
    .when('startDate', {
      is: (val: Date | null) => val !== null,
      then: (schema) =>
        schema.min(
          yup.ref('startDate'),
          'Data de fim deve ser posterior a data de inicio'
        ),
    }),
  observations: yup
    .string()
    .defined()
    .max(2000, 'Observacoes nao podem exceder 2000 caracteres'),
  activityProcess: yup
    .string()
    .defined()
    .max(2000, 'Atividade / processo não pode exceder 2000 caracteres'),
  riskMatrixType: yup
    .mixed<RiskMatrixType>()
    .nullable()
    .defined()
    .oneOf([...RISK_MATRIX_TYPES, null], 'Tipo de matriz invalido'),
  // Probabilidade e severidade podem ficar em branco: a janela salva incompleta e a completude
  // diz o que falta. Preenchidas, têm de caber na matriz escolhida.
  riskProbability: yup
    .number()
    .nullable()
    .defined()
    .transform((value, original) => (original === '' ? null : value))
    .test('prob-range', 'Probabilidade fora da faixa da matriz', function (value) {
      const type = this.parent.riskMatrixType as RiskMatrixType | null;
      if (value == null || type == null) return true;
      return Number.isInteger(value) && value >= 1 && value <= getMaxProbability(type);
    }),
  riskSeverity: yup
    .number()
    .nullable()
    .defined()
    .transform((value, original) => (original === '' ? null : value))
    .test('sev-range', 'Severidade fora da faixa da matriz', function (value) {
      const type = this.parent.riskMatrixType as RiskMatrixType | null;
      if (value == null || type == null) return true;
      return Number.isInteger(value) && value >= 1 && value <= getMaxSeverity(type);
    }),
  examTypes: yup.array().of(yup.string().required()).defined().default([]),
  reportTypes: yup.array().of(yup.string().required()).defined().default([]),
  existingAdministrativeMeasures: yup.array().of(yup.string().required()).defined().default([]),
  existingEngineeringMeasures: yup.array().of(yup.string().required()).defined().default([]),
  existingAdministrativeOther: yup.string().defined().max(2000, 'Máximo 2000 caracteres'),
  existingEngineeringOther: yup.string().defined().max(2000, 'Máximo 2000 caracteres'),
  existingEpiIds: yup.array().of(yup.number().required()).defined().default([]),
  proposedAdministrative: yup.string().defined().max(2000, 'Máximo 2000 caracteres'),
  proposedEngineering: yup.string().defined().max(2000, 'Máximo 2000 caracteres'),
  proposedEpi: yup.string().defined().max(2000, 'Máximo 2000 caracteres'),
  measurements: yup.array().of(yup.object({
    medicaoId: yup.number().nullable().defined(),
    measurementEquipmentId: yup.number().nullable().defined(),
    value: yup.number().nullable().defined().transform((v, o) => (o === '' ? null : v))
      .test('value-required', 'Informe o valor', function (v) {
        return v != null || isHazardousContext(this.options.context);
      })
      .test('value-positive', 'Valor deve ser maior que zero', (v) => v == null || v > 0),
    unit: yup.string().defined().max(50),
    measuredAt: yup.date().nullable().defined()
      .test('date-required', 'Informe a data', (v) => v != null)
      .test(NAO_E_FUTURO, 'Data não pode ser no futuro', aindaNaoAconteceu),
    observations: yup.string().defined().max(2000),
    technique: yup.string().defined().max(255, 'Máximo 255 caracteres'),
    appliedToleranceLimit: yup.string().defined().max(255, 'Máximo 255 caracteres'),
    quartzPercent: yup.string().optional()
      .test('quartzo-do-limite', 'Informe o % de quartzo, de 0 a 100', function (v) {
        if (!comFormulaNoContexto(this.options.context)) return true;
        const texto = (v ?? '').trim();
        const numero = Number(texto);
        return texto !== '' && Number.isFinite(numero) && numero >= 0 && numero <= 100;
      }),
    assessmentType: yup.mixed<MeasurementAssessmentType | ''>().oneOf([...MEASUREMENT_ASSESSMENT_TYPES, '']).defined(),
    storedQuantity: yup.number().nullable().defined().transform((v, o) => (o === '' ? null : v))
      .test('stored-positive', 'Quantidade deve ser maior que zero', (v) => v == null || v > 0),
    storedQuantityUnit: yup.string().defined().max(20, 'Máximo 20 caracteres'),
    transportedQuantity: yup.number().nullable().defined().transform((v, o) => (o === '' ? null : v))
      .test('transported-positive', 'Quantidade deve ser maior que zero', (v) => v == null || v > 0),
    transportedQuantityUnit: yup.string().defined().max(20, 'Máximo 20 caracteres'),
    riskArea: yup.string().defined().max(255, 'Máximo 255 caracteres'),
    storageType: yup.string().defined().max(255, 'Máximo 255 caracteres'),
    hazardousFraming: yup.boolean().defined()
      .test('framing-needs-annex', 'Informe o anexo da NR-16 que enquadra a atividade', function (v) {
        return v !== true || Boolean(this.parent.hazardousAnnex);
      }),
    hazardousAnnex: yup.string().defined().max(60, 'Máximo 60 caracteres')
      .test('annex-needs-framing', 'O anexo só vale com o enquadramento marcado', function (v) {
        return !v || this.parent.hazardousFraming === true;
      }),
    unhealthyFraming: yup.boolean().defined()
      .test('unhealthy-needs-degree', 'Informe o grau de insalubridade aplicado', function (v) {
        return v !== true || Boolean(this.parent.unhealthyDegreeApplied);
      }),
    unhealthyDegreeApplied: yup.string().defined()
      .test('degree-needs-framing', 'O grau só vale com o enquadramento marcado', function (v) {
        return !v || this.parent.unhealthyFraming === true;
      }),
  })).defined().default([]),
});

// ============================================================================
// COMPONENT
// ============================================================================

/** Fresh default form values (startDate is today in São Paulo, computed per open). */
function buildDefaultValues(): FormularioDoRisco {
  return {
    exposureLevel: ExposureLevel.MEDIUM,
    startDate: hojeNoSeletor(),
    endDate: null,
    observations: '',
    activityProcess: '',
    riskMatrixType: null,
    riskProbability: null,
    riskSeverity: null,
    examTypes: [],
    reportTypes: [],
    existingAdministrativeMeasures: [],
    existingEngineeringMeasures: [],
    existingAdministrativeOther: '',
    existingEngineeringOther: '',
    existingEpiIds: [],
    proposedAdministrative: '',
    proposedEngineering: '',
    proposedEpi: '',
    measurements: [],
  };
}

const ehNatureza = (valor: string | null | undefined): valor is MeasurementAssessmentType =>
  MEASUREMENT_ASSESSMENT_TYPES.includes(valor as MeasurementAssessmentType);

function medicaoDoFormulario(m: RiskApplicationMeasurementRow): MeasurementFormRow {
  return {
    medicaoId: Number(m.id),
    measurementEquipmentId: m.measurementEquipmentId ?? null,
    value: m.value != null ? Number(m.value) : null,
    unit: m.unit ?? '',
    measuredAt: m.measuredAt ? dataDoDia(m.measuredAt) : null,
    observations: m.observations ?? '',
    technique: m.technique ?? '',
    appliedToleranceLimit: m.appliedToleranceLimit ?? '',
    quartzPercent: m.quartzPercent != null ? String(m.quartzPercent) : '',
    assessmentType: ehNatureza(m.assessmentType) ? m.assessmentType : '',
    storedQuantity: m.storedQuantity != null ? Number(m.storedQuantity) : null,
    storedQuantityUnit: m.storedQuantityUnit ?? '',
    transportedQuantity: m.transportedQuantity != null ? Number(m.transportedQuantity) : null,
    transportedQuantityUnit: m.transportedQuantityUnit ?? '',
    riskArea: m.riskArea ?? '',
    storageType: m.storageType ?? '',
    hazardousFraming: m.hazardousFraming ?? false,
    hazardousAnnex: m.hazardousAnnex ?? '',
    unhealthyFraming: m.unhealthyFraming ?? false,
    unhealthyDegreeApplied: m.unhealthyDegreeApplied ?? '',
  };
}

/** O que a janela precisa saber do risco do catálogo, além do formulário. */
interface NaturezaDoRisco {
  unidadePadrao: string;
  perigoso: boolean;
  insalubre: boolean;
  grauPadrao: string;
  avaliacao: string | null;
  /** Sílica livre (#459): os parâmetros da conta; nulo nos demais riscos. */
  formulaDoLimite: FormulaDoLimiteParaTela | null;
}

const NATUREZA_VAZIA: NaturezaDoRisco = {
  unidadePadrao: '',
  perigoso: false,
  insalubre: false,
  grauPadrao: '',
  avaliacao: null,
  formulaDoLimite: null,
};

function naturezaDe(risco: {
  measurementUnit?: string | null;
  hazardousAgent?: string | null;
  unhealthyAgent?: string | null;
  unhealthyDegree?: string | null;
  assessmentType?: string | null;
  formulaDoLimite?: FormulaDoLimiteParaTela | null;
} | null | undefined): NaturezaDoRisco {
  return {
    unidadePadrao: risco?.measurementUnit ?? '',
    perigoso: Boolean(risco?.hazardousAgent?.trim()),
    insalubre: Boolean(risco?.unhealthyAgent?.trim()),
    grauPadrao: risco?.unhealthyDegree ?? '',
    avaliacao: risco?.assessmentType ?? null,
    formulaDoLimite: risco?.formulaDoLimite ?? null,
  };
}

export const RiskApplicationModal: React.FC<RiskApplicationModalProps> = (props) => {
  const displayRiskName = props.riskName ?? 'Risco';

  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [epiOptions, setEpiOptions] = useState<OpcaoDeEpi[]>([]);
  const [equipmentOptions, setEquipmentOptions] = useState<OpcaoDeAparelho[]>([]);
  const [natureza, setNatureza] = useState<NaturezaDoRisco>(NATUREZA_VAZIA);
  const [efetivos, setEfetivos] = useState<EfetivosDoRisco | null>(null);
  const [riskIdDaAplicacao, setRiskIdDaAplicacao] = useState<number | null>(null);
  // "Só nestes" escolhido e ainda sem documento marcado continua sendo "Só nestes" na tela.
  const [soEstesDocumentos, setSoEstesDocumentos] = useState(false);
  const [aba, setAba] = useState<SecaoDaJanela>('exposicao');
  // O campo que "Faltam" pediu: só se foca depois que a aba dele estiver à vista.
  const [campoPendente, setCampoPendente] = useState<string | null>(null);

  useEffect(() => {
    if (campoPendente === null) return;
    levarAoCampo(campoPendente);
    setCampoPendente(null);
  }, [aba, campoPendente]);

  const irAoCampo = (criterio: CriterioDaJanela) => {
    setAba(criterio.secao);
    setCampoPendente(criterio.alvo);
  };

  const aoFalharValidacao = (erros: FieldErrors<FormularioDoRisco>) => {
    const secao = primeiraSecaoComErro(erros);
    if (secao) setAba(secao);
  };

  const methods = useForm<FormularioDoRisco>({
    resolver: yupResolver(validationSchema),
    defaultValues: buildDefaultValues(),
    context: { isHazardousRisk: natureza.perigoso, comFormulaDoLimite: natureza.formulaDoLimite !== null },
  });
  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting },
  } = methods;

  // ============================================================================
  // POPULATE ON OPEN
  // ============================================================================

  // EPI marcado antes e que não está mais ligado ao risco continua à vista — marcado como tal —
  // para a pessoa poder desmarcá-lo; sem isso ele seria regravado sem caixa nenhuma na tela.
  const orphanOptions = (
    selectedEpis: RiskApplicationExistingEpiRow[] | undefined,
    activeIds: Set<number>,
  ): OpcaoDeEpi[] =>
    (selectedEpis ?? [])
      .filter((e) => e.epi && !activeIds.has(e.epiId))
      .map((e) => ({
        id: e.epi!.id,
        nome: e.epi!.nome,
        metodo: e.epi!.metodo,
        atenuacao: e.epi!.atenuacao ?? null,
        efficiency: e.epi!.efficiency ?? null,
        orphaned: true,
      }));

  const loadEpiOptions = (riskId: number, selectedEpis?: RiskApplicationExistingEpiRow[]) => {
    riskProtectionService.getProtections(riskId, { type: 'EPI' })
      .then((res) => {
        const active: OpcaoDeEpi[] = res.protections
          .filter((p) => p.epi)
          .map((p) => ({
            id: p.epi!.id,
            nome: p.epi!.nome,
            metodo: p.epi!.metodo,
            atenuacao: p.epi!.atenuacao ?? null,
            efficiency: p.epi!.efficiency ?? null,
          }));
        const activeIds = new Set(active.map((o) => o.id));
        setEpiOptions([...active, ...orphanOptions(selectedEpis, activeIds)]);
      })
      .catch(() => setEpiOptions(orphanOptions(selectedEpis, new Set())));
  };

  // Aparelho já usado e depois excluído continua com nome na linha, marcado como excluído (#221).
  const orphanEquipmentOptions = (
    selectedMeasurements: RiskApplicationMeasurementRow[] | undefined,
    activeIds: Set<number>,
  ): OpcaoDeAparelho[] => {
    const byId = new Map<number, OpcaoDeAparelho>();
    (selectedMeasurements ?? [])
      .filter((m) => m.equipment && !activeIds.has(m.equipment.id))
      .forEach((m) => {
        byId.set(m.equipment!.id, {
          id: m.equipment!.id,
          name: m.equipment!.name,
          serialNumber: m.equipment!.serialNumber ?? null,
          orphaned: true,
        });
      });
    return Array.from(byId.values());
  };

  const loadEquipmentOptions = (selectedMeasurements?: RiskApplicationMeasurementRow[]) => {
    measurementEquipmentService
      .getMeasurementEquipments({ status: MeasurementEquipmentStatus.ACTIVE, limit: 200 })
      .then((res) => {
        const active: OpcaoDeAparelho[] = res.equipments.map((e) => ({
          id: e.id,
          name: e.name,
          serialNumber: e.serialNumber ?? null,
        }));
        const activeIds = new Set(active.map((o) => o.id));
        setEquipmentOptions([...active, ...orphanEquipmentOptions(selectedMeasurements, activeIds)]);
      })
      .catch(() => setEquipmentOptions(orphanEquipmentOptions(selectedMeasurements, new Set())));
  };

  useEffect(() => {
    if (!props.open) return;

    setFetchError(null);
    setSubmitError(null);
    setEpiOptions([]);
    setEquipmentOptions([]);
    setNatureza(NATUREZA_VAZIA);
    setEfetivos(null);
    setRiskIdDaAplicacao(null);
    setSoEstesDocumentos(false);
    setAba('exposicao');
    setCampoPendente(null);

    buscarEfetivos<RiskApplication>('risk', props.scope, props.scopeId)
      .then(setEfetivos)
      .catch((err: unknown) => console.error('Erro ao ler o que vale para o destino:', err));

    if (props.mode === 'edit' && props.applicationId != null) {
      const id = props.applicationId;
      setFetchLoading(true);
      riskApplicationService
        .getById(id)
        .then((app) => {
          if (!app) {
            setFetchError('Aplicacao nao encontrada');
            return;
          }
          // A matriz da aplicação, ou a do risco — só quando completa (tipo, probabilidade e
          // severidade), para nunca pré-preencher uma matriz pela metade.
          const ownMatrix = pickCompleteMatrix(app);
          const matrix = ownMatrix.riskMatrixType != null ? ownMatrix : pickCompleteMatrix(app.risk);
          const documentos = app.reportTypes?.map((r) => r.reportType) ?? [];
          reset({
            ...buildDefaultValues(),
            exposureLevel: app.exposureLevel,
            startDate: app.startDate ? dataDoDia(app.startDate) : null,
            endDate: app.endDate ? dataDoDia(app.endDate) : null,
            observations: app.observations ?? '',
            activityProcess: app.activityProcess ?? '',
            ...matrix,
            examTypes: app.examTypes?.map((e) => e.examType) ?? [],
            reportTypes: documentos,
            existingAdministrativeMeasures: app.existingMeasures?.filter((m) => m.category === 'ADMINISTRATIVE').map((m) => m.measure) ?? [],
            existingEngineeringMeasures: app.existingMeasures?.filter((m) => m.category === 'ENGINEERING').map((m) => m.measure) ?? [],
            existingAdministrativeOther: app.existingAdministrativeOther ?? '',
            existingEngineeringOther: app.existingEngineeringOther ?? '',
            existingEpiIds: app.existingEpis?.map((e) => e.epiId) ?? [],
            proposedAdministrative: app.proposedAdministrative ?? '',
            proposedEngineering: app.proposedEngineering ?? '',
            proposedEpi: app.proposedEpi ?? '',
            measurements: app.measurements?.map(medicaoDoFormulario) ?? [],
          });
          setSoEstesDocumentos(documentos.length > 0);
          setRiskIdDaAplicacao(Number(app.riskId));
          loadEpiOptions(app.riskId, app.existingEpis);
          loadEquipmentOptions(app.measurements);
          setNatureza(naturezaDe(app.risk));
        })
        .catch((err: unknown) => {
          setFetchError(err instanceof Error ? err.message : 'Erro ao carregar aplicacao');
        })
        .finally(() => setFetchLoading(false));
    } else if (props.riskId != null) {
      // Aplicar: lê o risco e já abre com a matriz dele (completa), que a pessoa pode trocar.
      const riskId = props.riskId;
      setFetchLoading(true);
      occupationalRiskService
        .getOccupationalRiskById(riskId)
        .then((risk) => {
          reset({ ...buildDefaultValues(), ...pickCompleteMatrix(risk) });
          loadEpiOptions(riskId);
          loadEquipmentOptions();
          setNatureza(naturezaDe(risk));
        })
        .catch((err: unknown) => {
          setFetchError(err instanceof Error ? err.message : 'Erro ao carregar matriz do risco');
          reset(buildDefaultValues());
        })
        .finally(() => setFetchLoading(false));
    } else {
      reset(buildDefaultValues());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  // ============================================================================
  // COMPLETUDE
  // ============================================================================

  const valores = watch();
  const episEmUso = (valores.existingEpiIds ?? []).map((id) => {
    const opcao = epiOptions.find((o) => o.id === id);
    return { ca: opcao?.metodo, eficacia: opcao?.efficiency };
  });
  const criterios = criteriosDoRisco({
    exposureLevel: valores.exposureLevel ?? null,
    startDate: valores.startDate ?? null,
    activityProcess: valores.activityProcess ?? '',
    riskProbability: valores.riskProbability ?? null,
    riskSeverity: valores.riskSeverity ?? null,
    riskMatrixType: valores.riskMatrixType ?? null,
    existingAdministrativeMeasures: valores.existingAdministrativeMeasures ?? [],
    existingEngineeringMeasures: valores.existingEngineeringMeasures ?? [],
    existingAdministrativeOther: valores.existingAdministrativeOther ?? '',
    existingEngineeringOther: valores.existingEngineeringOther ?? '',
    episEmUso,
    riscoTemEpiNoCatalogo: epiOptions.some((o) => !o.orphaned),
    avaliacaoNoRisco: natureza.avaliacao,
    riscoComFormula: natureza.formulaDoLimite !== null,
    insalubre: natureza.insalubre,
    perigoso: natureza.perigoso,
    medicoes: valores.measurements ?? [],
  });
  const completude = completudeDe(criterios);
  const faltando = new Set(criterios.filter((c) => !c.preenchido).map((c) => c.alvo));

  // ============================================================================
  // SUBMIT
  // ============================================================================

  const orNull = (v: string) => (v.trim() ? v.trim() : null);

  /**
   * Os campos de periculosidade só viajam quando o risco é perigoso: o servidor recusa
   * preenchidos nos demais, e um resíduo de digitação barraria o salvamento inteiro.
   */
  const toMeasurementPayload = (m: MeasurementFormRow) => ({
    ...(m.medicaoId != null ? { id: m.medicaoId } : {}),
    measurementEquipmentId: m.measurementEquipmentId,
    value: m.value,
    unit: orNull(m.unit),
    measuredAt: m.measuredAt ? diaDaData(m.measuredAt) : '',
    observations: orNull(m.observations),
    technique: orNull(m.technique),
    // Em sílica o servidor calcula e grava o texto do limite; nos demais o quartzo nem viaja.
    appliedToleranceLimit: natureza.formulaDoLimite ? null : orNull(m.appliedToleranceLimit),
    ...(natureza.formulaDoLimite ? { quartzPercent: Number(m.quartzPercent) } : {}),
    assessmentType: m.assessmentType || null,
    storedQuantity: natureza.perigoso ? m.storedQuantity : null,
    storedQuantityUnit: natureza.perigoso ? orNull(m.storedQuantityUnit) : null,
    transportedQuantity: natureza.perigoso ? m.transportedQuantity : null,
    transportedQuantityUnit: natureza.perigoso ? orNull(m.transportedQuantityUnit) : null,
    riskArea: natureza.perigoso ? orNull(m.riskArea) : null,
    storageType: natureza.perigoso ? orNull(m.storageType) : null,
    hazardousFraming: natureza.perigoso ? m.hazardousFraming : false,
    hazardousAnnex: natureza.perigoso ? orNull(m.hazardousAnnex) : null,
    unhealthyFraming: natureza.insalubre ? m.unhealthyFraming : false,
    unhealthyDegreeApplied: natureza.insalubre ? orNull(m.unhealthyDegreeApplied) : null,
  });

  const onSubmit = async (data: FormularioDoRisco) => {
    setSubmitError(null);

    const startDateStr = data.startDate ? diaDaData(data.startDate) : hojeEmSaoPaulo();
    const endDateStr = data.endDate ? diaDaData(data.endDate) : null;

    const campos: CamposDaAplicacao = {
      exposureLevel: data.exposureLevel,
      startDate: startDateStr,
      endDate: endDateStr,
      observations: data.observations ?? null,
      activityProcess: orNull(data.activityProcess),
      // Matriz pela metade não se grava: o servidor recusa tipo sem probabilidade e severidade.
      ...(data.riskProbability != null && data.riskSeverity != null
        ? { riskMatrixType: data.riskMatrixType, riskProbability: data.riskProbability, riskSeverity: data.riskSeverity }
        : { riskMatrixType: null, riskProbability: null, riskSeverity: null }),
      examTypes: data.examTypes as ExamType[],
      reportTypes: (soEstesDocumentos ? data.reportTypes : []) as ReportType[],
      existingAdministrativeMeasures: data.existingAdministrativeMeasures,
      existingEngineeringMeasures: data.existingEngineeringMeasures,
      existingAdministrativeOther: orNull(data.existingAdministrativeOther),
      existingEngineeringOther: orNull(data.existingEngineeringOther),
      existingEpiIds: data.existingEpiIds,
      proposedAdministrative: orNull(data.proposedAdministrative),
      proposedEngineering: orNull(data.proposedEngineering),
      proposedEpi: orNull(data.proposedEpi),
      measurements: data.measurements.map(toMeasurementPayload),
    };

    try {
      if (props.mode === 'apply' && props.riskId != null) {
        await riskApplicationService.apply({ ...campos, riskId: props.riskId, scope: props.scope, scopeId: props.scopeId });
      } else if (props.mode === 'edit' && props.applicationId != null) {
        await riskApplicationService.update(props.applicationId, campos);
      }
      props.onSaved();
      props.onClose();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Erro ao salvar aplicacao');
    }
  };

  const isBusy = isSubmitting || fetchLoading;
  const shownError = submitError ?? fetchError;

  const riskIdAtual = props.mode === 'apply' ? props.riskId ?? null : riskIdDaAplicacao;
  const herdada =
    efetivos && riskIdAtual !== null
      ? efetivos.herdadas.find(
          (h) => mesmoId(h.riskId, riskIdAtual) && (props.mode === 'edit' || !h.substituidaNoDestino)
        )
      : undefined;
  const documentosEscolhidos = (valores.reportTypes ?? []) as ReportType[];
  const destino = efetivos?.destino;
  const titulo = destino
    ? `Dados de ${displayRiskName} ${referencia('em', destino.nivel, destino.nome)}`
    : `Dados de ${displayRiskName}`;
  const caminhoDoDestino = destino && (destino.nivel === 'company' ? destino.nome : `${destino.empresaNome} › ${destino.nome}`);

  const corpoDaSecao: Record<SecaoDaJanela, React.ReactNode> = {
    exposicao: <SecaoExposicao desabilitada={isBusy} />,
    medidas: <SecaoMedidasDeControle opcoesDeEpi={epiOptions} desabilitada={isBusy} riskId={riskIdAtual ?? undefined} />,
    medicoes: (
      <SecaoMedicoes
        opcoesDeAparelho={equipmentOptions}
        unidadePadrao={natureza.unidadePadrao}
        desabilitada={isBusy}
        perigoso={natureza.perigoso}
        insalubre={natureza.insalubre}
        grauPadrao={natureza.grauPadrao}
        nomeDoRisco={displayRiskName}
        faltando={faltando}
        formulaDoLimite={natureza.formulaDoLimite}
      />
    ),
    exames: <SecaoExames desabilitada={isBusy} />,
    documentos: (
      <>
        <EscolhaDeDocumentos
          soEstes={soEstesDocumentos}
          escolhidos={documentosEscolhidos}
          sugestao={efetivos?.marcacaoSugerida}
          aoMudar={(soEstes, escolhidos) => {
            setSoEstesDocumentos(soEstes);
            setValue('reportTypes', escolhidos, { shouldDirty: true });
          }}
        />
        {efetivos && riskIdAtual !== null && (
          <ResumoDoEfeito
            titulo="Ao salvar"
            linhas={linhasAoSalvar(efetivos, soEstesDocumentos ? documentosEscolhidos : [], herdada)}
          />
        )}
      </>
    ),
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Dialog
      open={props.open}
      onClose={isBusy ? undefined : props.onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle component="div">
        <Typography variant="h6" component="h2">
          {titulo}
        </Typography>
        {caminhoDoDestino && (
          <Typography variant="body2" color="text.secondary">
            {caminhoDoDestino}
          </Typography>
        )}
        {!fetchLoading && <CompletudeDaJanela completude={completude} criterios={criterios} aoEscolher={irAoCampo} />}
      </DialogTitle>

      <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, aoFalharValidacao)} noValidate>
        <DialogContent dividers>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            {shownError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setSubmitError(null); setFetchError(null); }}>
                {shownError}
              </Alert>
            )}

            {destino && herdada && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {avisoDeHeranca(props.mode, destino, herdada)}
              </Alert>
            )}

            {fetchLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <PontoDaCompletude />
                  conta para a completude
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="stretch">
                  <AbasDaJanela criterios={criterios} aba={aba} aoTrocar={setAba} />
                  <Box sx={{ flex: 1, minWidth: 0, minHeight: 420 }}>
                    {SECOES.map((secao) => (
                      <Box
                        key={secao.id}
                        role="tabpanel"
                        id={idDaSecao(secao.id)}
                        aria-labelledby={`aba-${secao.id}`}
                        hidden={aba !== secao.id}
                        tabIndex={-1}
                      >
                        <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                          {secao.titulo}
                        </Typography>
                        {corpoDaSecao[secao.id]}
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </>
            )}
          </LocalizationProvider>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            Salva tudo de uma vez; dá para salvar faltando informação e voltar depois.
          </Typography>
          <Button type="button" onClick={props.onClose} disabled={isBusy} color="inherit">
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isBusy}
            startIcon={isBusy && <CircularProgress size={16} />}
          >
            Salvar
          </Button>
        </DialogActions>
      </form>
      </FormProvider>
    </Dialog>
  );
};

export default RiskApplicationModal;
